/**
 * Die Koerperkarte als Zeichnung (F-34 C2): Vorder- und Rueckseite
 * nebeneinander, jede Muskelflaeche eingefaerbt, antippbar und per Tastatur
 * erreichbar. Der Text zu jeder Flaeche steht im Namen und im Tooltip.
 * Die Fugen zwischen den Muskeln und die Detaillinien sind in Hautfarbe
 * gezeichnet - wie auf einer anatomischen Muskeltafel.
 */
import { GRUPPEN_IDS, MUSKELN, type GruppeId, type MuskelId } from '@/domain/training/muskeln';
import { BREITE, HINTEN, HOEHE, KOPF, LINIEN_HINTEN, LINIEN_VORNE, OHR, SILHOUETTE, VORNE } from '@/modules/training/koerper/formen';

const SPIEGEL = `translate(${BREITE},0) scale(-1,1)`;
const HAUT = 'var(--color-koerper)';

export function Koerperbild({ farbe, label, detail, gewaehlt, onWahl }: {
  farbe: (m: MuskelId) => string;
  label: (m: MuskelId) => string;
  /** Detailstufe: jeder Muskel einzeln waehlbar; sonst die ganze Gruppe. */
  detail: boolean;
  gewaehlt: MuskelId | GruppeId | null;
  onWahl: (m: MuskelId) => void;
}) {
  const istGewaehlt = (m: MuskelId) => gewaehlt === m || (!detail && gewaehlt === MUSKELN[m].gruppe);
  return (
    <div className="grid grid-cols-2 gap-2">
      {([['Vorderseite', VORNE, LINIEN_VORNE], ['Rückseite', HINTEN, LINIEN_HINTEN]] as const).map(([titel, formen, linien]) => {
        // In der Uebersicht ein Tab-Halt je Gruppe und Seite, sonst je Muskel.
        const ersteJeGruppe = new Set(GRUPPEN_IDS.map((g) => (Object.keys(formen) as MuskelId[]).find((m) => MUSKELN[m].gruppe === g)).filter(Boolean));
        return (
          <figure key={titel} className="flex flex-col items-center gap-1">
            <svg viewBox={`0 0 ${BREITE} ${HOEHE}`} role="group" aria-label={titel} className="h-auto w-full max-w-64">
              <ellipse cx={OHR.cx} cy={OHR.cy} rx={OHR.rx} ry={OHR.ry} fill={HAUT} />
              <ellipse cx={OHR.cx} cy={OHR.cy} rx={OHR.rx} ry={OHR.ry} transform={SPIEGEL} fill={HAUT} />
              <ellipse cx={KOPF.cx} cy={KOPF.cy} rx={KOPF.rx} ry={KOPF.ry} fill={HAUT} />
              <path d={SILHOUETTE} fill={HAUT} />
              <path d={SILHOUETTE} transform={SPIEGEL} fill={HAUT} />
              {(Object.entries(formen) as [MuskelId, string[]][]).flatMap(([m, pfade]) => pfade.flatMap((d, i) => [false, true].map((spiegel) => {
                const fokus = !spiegel && i === 0 && (detail || ersteJeGruppe.has(m));
                const aktiv = istGewaehlt(m);
                return (
                  <path key={`${m}-${i}-${spiegel}`} d={d} {...(spiegel ? { transform: SPIEGEL } : {})}
                    fill={farbe(m)} stroke={aktiv ? 'var(--color-ink)' : HAUT} strokeWidth={aktiv ? 2.2 : 1.2} strokeLinejoin="round"
                    tabIndex={fokus ? 0 : -1} role={fokus ? 'button' : undefined} aria-label={fokus ? label(m) : undefined} aria-pressed={fokus ? aktiv : undefined}
                    onClick={() => onWahl(m)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onWahl(m); } }}
                    className="cursor-pointer outline-none transition-[filter] hover:brightness-125 focus-visible:brightness-150">
                    <title>{label(m)}</title>
                  </path>
                );
              })))}
              <g fill="none" stroke={HAUT} strokeWidth={1.2} strokeLinecap="round" pointerEvents="none" aria-hidden="true">
                {linien.flatMap((d, i) => [<path key={`l${i}`} d={d} />, <path key={`r${i}`} d={d} transform={SPIEGEL} />])}
              </g>
            </svg>
            <figcaption className="text-[11px] text-ink-muted">{titel}</figcaption>
          </figure>
        );
      })}
    </div>
  );
}
